import * as React from 'react';
import { Dropzone } from '../dropzone';
import { useFormFieldContext } from './form-field-context';
import { useFieldAria } from './use-field-aria';

/**
 * File upload control (Figma dropzone 732:18322). The RHF field value is a
 * `File | null` (single) or `File[]` (multiple); rendering is the shared
 * `Dropzone` — this only binds the field value, label association, and
 * invalid state. Non-RHF callers should use `Dropzone` directly.
 */
export function FormFieldUpload({
  multiple = false,
  accept,
  maxBytes,
  onRejected,
  title,
  description,
  browseLabel,
  editLabel,
  removeLabel,
  className,
}: {
  multiple?: boolean;
  accept?: string;
  /**
   * Size ceiling per file, in bytes. Opt-in — unlike the image grid this
   * control is not image-specific, so it imposes no limit unless given one.
   * Pass `MAX_IMAGE_BYTES` from `@noor/lib` for an image picker.
   */
  maxBytes?: number;
  /** Names of files rejected for exceeding `maxBytes`; the caller surfaces it. */
  onRejected?: (fileNames: string[]) => void;
  /** Dropzone title (20px semibold). */
  title?: React.ReactNode;
  /** Format hint under the title (14px regular). */
  description?: React.ReactNode;
  /** Brand-blue browse action label. */
  browseLabel?: React.ReactNode;
  /** Filled-state menu: replace-image action label. */
  editLabel?: string;
  /** Filled-state menu: delete-image action label. */
  removeLabel?: string;
  className?: string;
}) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();

  return (
    <Dropzone
      value={field.value as File | File[] | null | undefined}
      onFiles={(files) => field.onChange(multiple ? files : (files[0] ?? null))}
      multiple={multiple}
      id={aria.id}
      describedBy={aria['aria-describedby']}
      invalid={aria['aria-invalid']}
      onBlur={field.onBlur}
      {...(accept !== undefined ? { accept } : {})}
      {...(maxBytes !== undefined ? { maxBytes } : {})}
      {...(onRejected !== undefined ? { onRejected } : {})}
      {...(title !== undefined ? { title } : {})}
      {...(description !== undefined ? { description } : {})}
      {...(browseLabel !== undefined ? { browseLabel } : {})}
      {...(editLabel !== undefined ? { editLabel } : {})}
      {...(removeLabel !== undefined ? { removeLabel } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
